<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯簽到配置' : '新增簽到配置' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel">取消</button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          <span v-if="submitting">
            <i class="ri-loader-4-line spin"></i>
            {{ isEdit ? '更新中...' : '建立中...' }}
          </span>
          <span v-else>
            {{ isEdit ? '更新配置' : '建立配置' }}
          </span>
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="form-container">
      <!-- 頁籤導航（僅在編輯模式下顯示） -->
      <div v-if="isEdit" class="tabs-nav">
        <button
          :class="['tab-btn', { active: activeTab === 'basic' }]"
          @click="activeTab = 'basic'"
        >
          <i class="ri-information-line"></i>
          基本資訊
        </button>
        <button
          :class="['tab-btn', { active: activeTab === 'rewards' }]"
          @click="activeTab = 'rewards'"
        >
          <i class="ri-gift-line"></i>
          獎勵管理
        </button>
        <button
          :class="['tab-btn', { active: activeTab === 'visual' }]"
          @click="activeTab = 'visual'"
        >
          <i class="ri-palette-line"></i>
          視覺配置
        </button>
        <button
          :class="['tab-btn', { active: activeTab === 'records' }]"
          @click="activeTab = 'records'"
        >
          <i class="ri-file-list-line"></i>
          簽到記錄
        </button>
        <button
          :class="['tab-btn', { active: activeTab === 'stats' }]"
          @click="activeTab = 'stats'"
        >
          <i class="ri-bar-chart-line"></i>
          統計
        </button>
      </div>

      <!-- 頁籤內容 -->
      <div class="tabs-content">
        <!-- 基本資訊頁籤 -->
        <div v-show="!isEdit || activeTab === 'basic'" class="tab-pane">
          <form @submit.prevent="handleSubmit">
            <div class="form-section">
              <h3 class="section-title">基本資訊</h3>
              
              <div class="form-row">
                <div class="form-group">
                  <label class="required">簽到活動名稱</label>
                  <input
                    v-model="form.configName"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.configName }"
                    placeholder="例如：每日簽到活動"
                    required
                  />
                  <div v-if="errors.configName" class="invalid-feedback">
                    {{ errors.configName }}
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label>活動規則</label>
                <textarea
                  v-model="form.description"
                  class="form-control"
                  rows="8"
                  placeholder="請輸入活動規則（每行一條規則）"
                ></textarea>
                <p class="form-hint">每行輸入一條規則，將自動顯示為列表</p>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>活動開始時間</label>
                  <input
                    v-model="form.startDate"
                    type="datetime-local"
                    class="form-control"
                    :class="{ 'is-invalid': errors.startDate }"
                  />
                  <div v-if="errors.startDate" class="invalid-feedback">
                    {{ errors.startDate }}
                  </div>
                  <p class="form-hint">留空表示立即開始</p>
                </div>

                <div class="form-group">
                  <label>活動結束時間</label>
                  <input
                    v-model="form.endDate"
                    type="datetime-local"
                    class="form-control"
                    :class="{ 'is-invalid': errors.endDate }"
                  />
                  <div v-if="errors.endDate" class="invalid-feedback">
                    {{ errors.endDate }}
                  </div>
                  <p class="form-hint">留空表示無限期</p>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>每日簽到</label>
                  <div
                    class="checkin-toggle-row"
                    :class="{ 'is-invalid': errors.checkinEnabled }"
                  >
                    <label class="checkin-toggle-switch">
                      <input v-model="form.dailyCheckinEnabled" type="checkbox" />
                      <span class="checkin-toggle-slider"></span>
                    </label>
                    <span
                      class="checkin-toggle-status"
                      :class="form.dailyCheckinEnabled ? 'on' : 'off'"
                    >
                      {{ form.dailyCheckinEnabled ? '已啟用' : '已停用' }}
                    </span>
                  </div>
                  <div v-if="errors.checkinEnabled" class="invalid-feedback">
                    {{ errors.checkinEnabled }}
                  </div>
                </div>
              </div>

              <div v-if="!form.dailyCheckinEnabled && !form.cumulativeEnabled" class="alert alert-warning">
                <i class="ri-alert-line"></i>
                <span>每日簽到和累積簽到至少要有一個啟用，才能儲存配置。</span>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>累積簽到</label>
                  <div
                    class="checkin-toggle-row"
                    :class="{ 'is-invalid': errors.checkinEnabled }"
                  >
                    <label class="checkin-toggle-switch">
                      <input v-model="form.cumulativeEnabled" type="checkbox" />
                      <span class="checkin-toggle-slider"></span>
                    </label>
                    <span
                      class="checkin-toggle-status"
                      :class="form.cumulativeEnabled ? 'on' : 'off'"
                    >
                      {{ form.cumulativeEnabled ? '已啟用' : '已停用' }}
                    </span>
                  </div>
                  <div v-if="errors.checkinEnabled" class="invalid-feedback">
                    {{ errors.checkinEnabled }}
                  </div>
                </div>

                <div v-if="form.cumulativeEnabled" class="form-group">
                  <label>累積類型</label>
                  <select
                    v-model="form.cumulativeType"
                    class="form-control"
                  >
                    <option value="total">總累積</option>
                    <option value="consecutive">連續</option>
                  </select>
                </div>
              </div>

              <div class="form-group">
                <label>累積重置類型</label>
                <select
                  v-model="form.resetType"
                  class="form-control"
                >
                  <option value="never">不重置</option>
                  <option value="monthly">每月重置</option>
                  <option value="yearly">每年重置</option>
                  <option value="on_complete">達成後重置</option>
                </select>
              </div>

              <div class="form-group">
                <label>狀態</label>
                <div
                  class="checkin-toggle-row"
                  :class="{ 'is-invalid': errors.isActive }"
                >
                  <label class="checkin-toggle-switch">
                    <input v-model="form.isActive" type="checkbox" />
                    <span class="checkin-toggle-slider"></span>
                  </label>
                  <span class="checkin-toggle-status" :class="form.isActive ? 'on' : 'off'">
                    {{ form.isActive ? '已啟用' : '已停用' }}
                  </span>
                </div>
                <div v-if="errors.isActive" class="invalid-feedback">
                  {{ errors.isActive }}
                </div>
              </div>

              <div class="form-group">
                <MemberSourceSelector
                  v-model="form.memberSource"
                  :client-member-system-type="clientMemberSystemType"
                  :required="true"
                  :show-mode-lock-notice="true"
                  lockline-hint="僅允許選擇來源為 LOCKLINE 的會員參與簽到"
                  external-hint="不判斷會員身份，直接接受並記錄 m 傳來的值，只做簽到顯示與記錄"
                  wixtar-hint="僅允許選擇來源為 Wixtar 的會員參與簽到"
                  :error-message="errors.memberSource"
                />
              </div>

              <!-- 路由連結區塊（僅在編輯模式下顯示） -->
              <div v-if="isEdit" class="form-section">
                <h4 class="subsection-title">活動路由連結</h4>
                <p class="form-hint" style="margin-bottom: 16px;">複製以下連結分享給用戶，或點擊在新視窗開啟</p>
                
                <div class="route-links-container">
                  <!-- 簽到路由 -->
                  <div class="route-link-item">
                    <label class="route-label">
                      <i class="ri-calendar-check-line"></i>
                      簽到活動
                    </label>
                    <div class="route-input-group">
                      <input
                        :value="checkinRouteUrl"
                        type="text"
                        class="form-control route-input"
                        readonly
                        @click="$event.target.select()"
                      />
                      <div class="route-actions">
                        <button
                          type="button"
                          class="btn btn-sm btn-secondary"
                          @click="copyToClipboard(checkinRouteUrl, '簽到活動')"
                          :title="'複製簽到活動連結'"
                        >
                          <i class="ri-file-copy-line"></i>
                          複製
                        </button>
                        <a
                          :href="checkinTestModeUrl"
                          target="_blank"
                          class="btn btn-sm btn-primary"
                          :title="'在新視窗開啟簽到活動（測試模式）'"
                        >
                          <i class="ri-test-tube-line"></i>
                          測試預覽
                        </a>
                      </div>
                    </div>
                  </div>

                  <!-- 活動規則路由 -->
                  <div class="route-link-item">
                    <label class="route-label">
                      <i class="ri-file-text-line"></i>
                      活動規則
                    </label>
                    <div class="route-input-group">
                      <input
                        :value="ruleRouteUrl"
                        type="text"
                        class="form-control route-input"
                        readonly
                        @click="$event.target.select()"
                      />
                      <div class="route-actions">
                        <button
                          type="button"
                          class="btn btn-sm btn-secondary"
                          @click="copyToClipboard(ruleRouteUrl, '活動規則')"
                          :title="'複製活動規則連結'"
                        >
                          <i class="ri-file-copy-line"></i>
                          複製
                        </button>
                        <a
                          :href="ruleTestModeUrl"
                          target="_blank"
                          class="btn btn-sm btn-primary"
                          :title="'在新視窗開啟活動規則（測試模式）'"
                        >
                          <i class="ri-test-tube-line"></i>
                          測試預覽
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </form>
        </div>

        <!-- 獎勵管理頁籤 -->
        <div v-if="isEdit && activeTab === 'rewards'" class="tab-pane">
          <div class="rewards-section">
            <!-- 每日簽到獎勵 -->
            <div class="reward-block">
              <div class="section-header">
                <h3 class="section-title">每日簽到獎勵</h3>
                <span :class="form.dailyCheckinEnabled ? 'status-badge status-enabled' : 'status-badge status-disabled'">
                  <i :class="form.dailyCheckinEnabled ? 'ri-checkbox-circle-fill' : 'ri-close-circle-fill'"></i>
                  {{ form.dailyCheckinEnabled ? '已啟用' : '未啟用' }}
                </span>
              </div>

              <div v-if="!form.dailyCheckinEnabled" class="alert alert-warning">
                <i class="ri-alert-line"></i>
                <span>每日簽到功能目前未啟用，請先到「基本資訊」頁籤啟用每日簽到功能。</span>
              </div>

              <div v-else class="form-group">
                <label>抽獎配置</label>
                <select
                  v-model="form.dailyCheckinLotteryConfigId"
                  class="form-control"
                  :class="{ 'is-invalid': form.dailyCheckinLotteryConfigId && !isLotteryConfigValid(form.dailyCheckinLotteryConfigId) }"
                  :disabled="!form.dailyCheckinEnabled"
                  @change="handleDailyRewardChange"
                >
                  <option :value="null">請選擇抽獎配置</option>
                  <option
                    v-for="lottery in lotteryConfigs"
                    :key="lottery.id"
                    :value="lottery.id"
                    :disabled="!isLotteryConfigValid(lottery.id)"
                  >
                    {{ lottery.configName }}
                    <span v-if="!isLotteryConfigValid(lottery.id)"> (會員來源不匹配)</span>
                  </option>
                </select>
                <div v-if="form.dailyCheckinLotteryConfigId && !isLotteryConfigValid(form.dailyCheckinLotteryConfigId)" class="invalid-feedback">
                  <span v-if="form.memberSource === 'WIXTAR'">此簽到活動僅允許 Wixtar 會員，因此只能選擇 Wixtar 會員的抽獎配置</span>
                  <span v-else-if="form.memberSource === 'LOCKLINE'">此簽到活動僅允許 LOCKLINE 會員，因此只能選擇 LOCKLINE 會員的抽獎配置</span>
                  <span v-else-if="form.memberSource === 'EXTERNAL'">此簽到活動允許外部會員，因此只能選擇外部會員的抽獎配置</span>
                </div>
                <p class="form-hint">
                  <span v-if="form.memberSource === 'WIXTAR'">此簽到活動僅允許 Wixtar 會員，因此只能選擇 Wixtar 會員的抽獎配置</span>
                  <span v-else-if="form.memberSource === 'LOCKLINE'">此簽到活動僅允許 LOCKLINE 會員，因此只能選擇 LOCKLINE 會員的抽獎配置</span>
                  <span v-else-if="form.memberSource === 'EXTERNAL'">此簽到活動允許外部會員，因此只能選擇外部會員的抽獎配置</span>
                  <span v-else>每日簽到成功後觸發的抽獎活動</span>
                </p>
                <p v-if="!form.dailyCheckinLotteryConfigId" class="form-hint warning">
                  <i class="ri-information-line"></i>
                  請選擇一個抽獎配置，否則每日簽到將不會觸發任何獎勵。
                </p>
              </div>
            </div>

            <!-- 累積簽到獎勵 -->
            <div class="reward-block">
              <div class="section-header">
                <h3 class="section-title">累積簽到目標</h3>
                <div class="header-actions">
                  <span :class="form.cumulativeEnabled ? 'status-badge status-enabled' : 'status-badge status-disabled'">
                    <i :class="form.cumulativeEnabled ? 'ri-checkbox-circle-fill' : 'ri-close-circle-fill'"></i>
                    {{ form.cumulativeEnabled ? '已啟用' : '未啟用' }}
                  </span>
                  <button 
                    class="btn btn-primary" 
                    @click="handleAddReward"
                    :disabled="!form.cumulativeEnabled"
                  >
                    <i class="ri-add-line"></i>
                    新增目標
                  </button>
                </div>
              </div>

              <div v-if="!form.cumulativeEnabled" class="alert alert-warning">
                <i class="ri-alert-line"></i>
                <span>累積簽到功能目前未啟用，請先到「基本資訊」頁籤啟用累積簽到功能。</span>
              </div>

              <div v-else class="cumulative-info">
                <div class="info-badge">
                  <i class="ri-information-line"></i>
                  <span>累積類型：<strong>{{ form.cumulativeType === 'total' ? '總累積' : '連續' }}</strong></span>
                </div>
                <p class="form-hint">
                  {{ form.cumulativeType === 'total' 
                    ? '動作將根據總累積簽到天數觸發，同一天數可設定多組動作' 
                    : '動作將根據連續簽到天數觸發，同一天數可設定多組動作' }}
                </p>
              </div>

              <div v-if="!form.cumulativeEnabled" class="empty-state disabled">
                <p>累積簽到功能未啟用，無法設定累積簽到目標</p>
              </div>

              <div v-else-if="loadingRewards" class="loading-state">
                <i class="ri-loader-4-line spin"></i> 載入中...
              </div>

              <div v-else-if="rewards.length === 0" class="empty-state">
                <p>尚未設定累積簽到目標</p>
                <button class="btn btn-primary" @click="handleAddReward">
                  新增第一個目標
                </button>
              </div>

              <div v-else class="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>達成天數</th>
                      <th>動作類型</th>
                      <th>動作內容</th>
                      <th>狀態</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="group in groupedRewardList" :key="group.days">
                      <tr v-for="(reward, index) in group.rewards" :key="reward.id">
                        <td v-if="index === 0" :rowspan="group.rewards.length" class="days-cell">
                          <div class="days-cell-content">
                            <span>{{ group.days }} 天</span>
                            <button
                              type="button"
                              class="btn btn-sm btn-outline-primary btn-add-same-day"
                              @click="handleAddRewardForDays(group.days)"
                            >
                              <i class="ri-add-line"></i>
                              新增動作
                            </button>
                          </div>
                        </td>
                        <td>{{ getRewardTypeText(reward.rewardType) }}</td>
                        <td>
                          <div>
                            {{ getRewardContent(reward) }}
                            <span 
                              v-if="reward.rewardType === 'lottery' && reward.lotteryConfigId && !isLotteryConfigValid(reward.lotteryConfigId)" 
                              class="text-danger"
                              style="margin-left: 8px; font-size: 12px;"
                            >
                              <i class="ri-error-warning-line"></i> 會員來源不匹配
                            </span>
                          </div>
                        </td>
                        <td>
                          <span :class="reward.isActive ? 'badge badge-success' : 'badge badge-secondary'">
                            {{ reward.isActive ? '啟用' : '停用' }}
                          </span>
                        </td>
                        <td>
                          <div class="action-buttons">
                            <button class="btn btn-sm btn-secondary" @click="handleEditReward(reward)">
                              編輯
                            </button>
                            <button class="btn btn-sm btn-danger" @click="handleDeleteReward(reward.id)">
                              刪除
                            </button>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- 目標編輯 Modal -->
          <div v-if="showRewardModal" class="modal-overlay" @click.self="closeRewardModal">
            <div class="modal-content">
              <div class="modal-header">
                <h3>{{ editingReward ? '編輯動作' : (rewardFormPresetDays ? `新增動作（${rewardFormPresetDays} 天）` : '新增目標') }}</h3>
                <button class="btn-close" @click="closeRewardModal">
                  <i class="ri-close-line"></i>
                </button>
              </div>
              <div class="modal-body">
                <div class="form-group">
                  <label>達成天數 <span class="text-danger">*</span></label>
                  <input
                    v-model.number="rewardForm.days"
                    type="number"
                    class="form-control"
                    min="1"
                    placeholder="例如：7"
                  />
                  <p class="form-hint">累積簽到達到此天數時觸發動作</p>
                </div>

                <div class="form-group">
                  <label>動作類型 <span class="text-danger">*</span></label>
                  <select
                    v-model="rewardForm.rewardType"
                    class="form-control"
                    @change="handleRewardTypeChange"
                  >
                    <option value="point">點數</option>
                    <option value="coupon">優惠券</option>
                    <option value="lottery">抽獎</option>
                    <option value="tag">貼標</option>
                    <option value="message">發送訊息</option>
                    <option v-if="isWixtarClient || rewardForm.rewardType === 'wixtar_ticket'" value="wixtar_ticket">Wixtar 票券</option>
                  </select>
                </div>

                <div v-if="rewardForm.rewardType === 'lottery'" class="form-group">
                  <label>抽獎配置 <span class="text-danger">*</span></label>
                  <select
                    v-model="rewardForm.lotteryConfigId"
                    class="form-control"
                    :class="{ 'is-invalid': rewardForm.lotteryConfigId && !isLotteryConfigValid(rewardForm.lotteryConfigId) }"
                  >
                    <option :value="null">請選擇抽獎配置</option>
                    <option
                      v-for="lottery in lotteryConfigs"
                      :key="lottery.id"
                      :value="lottery.id"
                      :disabled="!isLotteryConfigValid(lottery.id)"
                    >
                      {{ lottery.configName }}
                      <span v-if="!isLotteryConfigValid(lottery.id)"> (會員來源不匹配)</span>
                    </option>
                  </select>
                <div v-if="rewardForm.lotteryConfigId && !isLotteryConfigValid(rewardForm.lotteryConfigId)" class="invalid-feedback">
                  <span v-if="form.memberSource === 'WIXTAR'">此簽到活動僅允許 Wixtar 會員，因此只能選擇 Wixtar 會員的抽獎配置</span>
                  <span v-else-if="form.memberSource === 'LOCKLINE'">此簽到活動僅允許 LOCKLINE 會員，因此只能選擇 LOCKLINE 會員的抽獎配置</span>
                  <span v-else-if="form.memberSource === 'EXTERNAL'">此簽到活動允許外部會員，因此只能選擇外部會員的抽獎配置</span>
                </div>
                  <p class="form-hint">
                    <span v-if="form.memberSource === 'WIXTAR'">此簽到活動僅允許 Wixtar 會員，因此只能選擇 Wixtar 會員的抽獎配置</span>
                    <span v-else-if="form.memberSource === 'LOCKLINE'">此簽到活動僅允許 LOCKLINE 會員，因此只能選擇 LOCKLINE 會員的抽獎配置</span>
                    <span v-else-if="form.memberSource === 'EXTERNAL'">此簽到活動允許外部會員，因此只能選擇外部會員的抽獎配置</span>
                    <span v-else>選擇抽獎配置</span>
                  </p>
                </div>

                <div v-if="rewardForm.rewardType === 'point'" class="form-group">
                  <label>點數 <span class="text-danger">*</span></label>
                  <input
                    v-model.number="rewardForm.rewardData.points"
                    type="number"
                    class="form-control"
                    min="1"
                    placeholder="例如：100"
                  />
                </div>

                <div v-if="rewardForm.rewardType === 'coupon'" class="form-group">
                  <label>優惠券 ID <span class="text-danger">*</span></label>
                  <input
                    v-model="rewardForm.rewardData.couponId"
                    type="text"
                    class="form-control"
                    placeholder="優惠券 ID"
                  />
                  <RewardStockWarning :template="couponTemplates.find((item) => item.id === rewardForm.rewardData.couponId)" />
                </div>

                <div v-if="rewardForm.rewardType === 'tag'" class="form-group">
                  <label>要貼上的標籤 <span class="text-danger">*</span></label>
                  <TagSelectorMultiple
                    v-model="rewardForm.rewardData.tagIds"
                    label=""
                    hint="會員達成此簽到階段時自動貼上"
                  />
                </div>

                <div v-if="rewardForm.rewardType === 'message'" class="form-group line-msg-picker-block">
                  <label>LINE 訊息範本 <span class="text-danger">*</span></label>
                  <LineMsgTemplatePicker
                    :key="`checkin-reward-msg-${editingReward?.id || 'new'}`"
                    audience-filter="personal"
                    :model-value="lineMsgTemplate"
                    :selected-id="rewardForm.rewardData.templateCode || null"
                    @update:model-value="onMessageTemplateChange"
                  />
                  <div class="picker-goto-link">
                    <router-link :to="{ name: 'LineMsgTemplateCreate' }" target="_blank">
                      <i class="ri-add-line"></i> 新增 LINE 訊息範本
                    </router-link>
                  </div>
                </div>

                <div v-if="rewardForm.rewardType === 'wixtar_ticket'" class="form-group">
                  <label>Wixtar 票券 <span class="text-danger">*</span></label>
                  <WixtarTicketMultiSelect
                    :model-value="rewardForm.rewardData.wixtarTicketIds || []"
                    :tickets="wixtarTickets"
                    :loading="wixtarTicketsLoading"
                    @update:model-value="rewardForm.rewardData.wixtarTicketIds = $event"
                  />
                  <p class="form-hint">可選擇多張票券，會員需已綁定 Wixtar。</p>
                </div>

                <div class="form-group">
                  <label>
                    <input
                      v-model="rewardForm.isActive"
                      type="checkbox"
                      class="form-check-input"
                    />
                    啟用此目標
                  </label>
                </div>
              </div>
              <div class="modal-footer">
                <button class="btn btn-secondary" @click="closeRewardModal">
                  取消
                </button>
                <button class="btn btn-primary" @click="handleSubmitReward" :disabled="submittingReward">
                  <span v-if="submittingReward">
                    <i class="ri-loader-4-line spin"></i> 儲存中...
                  </span>
                  <span v-else>儲存</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 視覺配置頁籤 -->
        <div v-if="isEdit && activeTab === 'visual'" class="tab-pane">
          <div class="visual-config-section">
            <div class="section-header">
              <div>
                <h3 class="section-title">視覺配置</h3>
                <p class="text-muted">設定簽到活動的視覺元素和圖片</p>
              </div>
              <button
                type="button"
                class="btn btn-primary"
                :disabled="savingVisualConfig"
                @click="saveVisualConfig"
              >
                <span v-if="savingVisualConfig">
                  <i class="ri-loader-4-line spin"></i> 儲存中...
                </span>
                <span v-else>
                  <i class="ri-save-line"></i> 儲存配置
                </span>
              </button>
            </div>

            <div v-if="loadingVisualConfig" class="loading-state">
              <i class="ri-loader-4-line spin"></i> 載入中...
            </div>

            <div v-else class="visual-config-editor">
              <div class="form-section">
                <h4 class="subsection-title">背景設定</h4>
                
                <div class="form-group">
                  <label>背景圖片</label>
                  <div class="image-upload-group">
                    <div v-if="visualConfig.backgroundImageUrl" class="image-preview">
                      <img 
                        :src="visualConfig.backgroundImageUrl" 
                        :key="visualConfig.backgroundImageUrl"
                        alt="背景圖片"
                        @error="handleImageError('backgroundImageUrl', $event)"
                        @load="handleImageLoad('backgroundImageUrl')"
                      />
                      <button
                        type="button"
                        class="btn-remove-image"
                        @click="removeImage('backgroundImageUrl')"
                      >
                        <i class="ri-close-line"></i>
                      </button>
                    </div>
                    <label class="btn-upload">
                      <input
                        type="file"
                        accept="image/*"
                        @change="handleImageSelect('backgroundImageUrl', $event)"
                      />
                      <i class="ri-upload-line"></i>
                      {{ visualConfig.backgroundImageUrl ? '更換圖片' : '上傳圖片' }}
                    </label>
                  </div>
                  <p class="form-hint">簽到活動的背景圖片，建議尺寸：1280 x 550px</p>
                </div>

                <!-- 背景圖片遮罩 -->
                <div class="form-group">
                  <label>背景圖片遮罩</label>
                  <div class="image-upload-group">
                    <div v-if="visualConfig.backgroundImageMaskUrl" class="image-preview">
                      <img 
                        :src="visualConfig.backgroundImageMaskUrl" 
                        :key="visualConfig.backgroundImageMaskUrl"
                        alt="背景圖片遮罩"
                        @error="handleImageError('backgroundImageMaskUrl', $event)"
                        @load="handleImageLoad('backgroundImageMaskUrl')"
                      />
                      <button
                        type="button"
                        class="btn-remove-image"
                        @click="removeImage('backgroundImageMaskUrl')"
                      >
                        <i class="ri-close-line"></i>
                      </button>
                    </div>
                    <label class="btn-upload">
                      <input
                        type="file"
                        accept="image/*"
                        @change="handleImageSelect('backgroundImageMaskUrl', $event)"
                      />
                      <i class="ri-upload-line"></i>
                      {{ visualConfig.backgroundImageMaskUrl ? '更換圖片' : '上傳圖片' }}
                    </label>
                  </div>
                  <p class="form-hint">背景圖片遮罩（可選，當背景圖片不夠大時使用），建議尺寸：1280 x 550px</p>
                </div>

                <div class="form-group">
                  <label>背景顏色</label>
                  <div class="color-input-group">
                    <input
                      v-model="visualConfig.backgroundColor"
                      type="color"
                      class="form-control color-picker"
                    />
                    <input
                      v-model="visualConfig.backgroundColor"
                      type="text"
                      class="form-control color-text"
                      placeholder="#f3f3f3"
                      pattern="^#[0-9A-Fa-f]{6}$"
                    />
                  </div>
                  <p class="form-hint">背景顏色（當沒有背景圖片時使用）</p>
                </div>

                <div class="form-group">
                  <label>框線顏色</label>
                  <div class="color-input-group">
                    <input
                      v-model="visualConfig.borderColor"
                      type="color"
                      class="form-control color-picker"
                    />
                    <input
                      v-model="visualConfig.borderColor"
                      type="text"
                      class="form-control color-text"
                      placeholder="#000000"
                      pattern="^#[0-9A-Fa-f]{6}$"
                    />
                  </div>
                  <p class="form-hint">框線顏色設定</p>
                </div>

                <div class="form-group">
                  <label>訊息按鈕文字顏色</label>
                  <div class="color-input-group">
                    <input
                      v-model="visualConfig.messageButtonTextColor"
                      type="color"
                      class="form-control color-picker"
                    />
                    <input
                      v-model="visualConfig.messageButtonTextColor"
                      type="text"
                      class="form-control color-text"
                      placeholder="#ffffff"
                      pattern="^#[0-9A-Fa-f]{6}$"
                    />
                  </div>
                  <p class="form-hint">訊息按鈕的文字顏色</p>
                </div>

                <div class="form-group">
                  <label>訊息按鈕背景顏色</label>
                  <div class="color-input-group">
                    <input
                      v-model="visualConfig.messageButtonBackgroundColor"
                      type="color"
                      class="form-control color-picker"
                    />
                    <input
                      v-model="visualConfig.messageButtonBackgroundColor"
                      type="text"
                      class="form-control color-text"
                      placeholder="#007bff"
                      pattern="^#[0-9A-Fa-f]{6}$"
                    />
                  </div>
                  <p class="form-hint">訊息按鈕的背景顏色</p>
                </div>
              </div>

              <div class="form-section">
                <h4 class="subsection-title">按鈕圖片</h4>
                
                <div class="form-group">
                  <label>規則按鈕圖片</label>
                  <div class="image-upload-group">
                    <div v-if="visualConfig.ruleButtonImageUrl" class="image-preview">
                      <img 
                        :src="visualConfig.ruleButtonImageUrl" 
                        :key="visualConfig.ruleButtonImageUrl"
                        alt="規則按鈕圖片"
                        @error="handleImageError('ruleButtonImageUrl', $event)"
                        @load="handleImageLoad('ruleButtonImageUrl')"
                      />
                      <button
                        type="button"
                        class="btn-remove-image"
                        @click="removeImage('ruleButtonImageUrl')"
                      >
                        <i class="ri-close-line"></i>
                      </button>
                    </div>
                    <label class="btn-upload">
                      <input
                        type="file"
                        accept="image/*"
                        @change="handleImageSelect('ruleButtonImageUrl', $event)"
                      />
                      <i class="ri-upload-line"></i>
                      {{ visualConfig.ruleButtonImageUrl ? '更換圖片' : '上傳圖片' }}
                    </label>
                  </div>
                  <p class="form-hint">右上角的規則按鈕圖片</p>
                </div>

                <div class="form-group">
                  <label>簽到玩遊戲按鈕圖片（未簽到）</label>
                  <div class="image-upload-group">
                    <div v-if="visualConfig.gameBannerImageUrl" class="image-preview">
                      <img 
                        :src="visualConfig.gameBannerImageUrl" 
                        :key="visualConfig.gameBannerImageUrl"
                        alt="簽到玩遊戲按鈕圖片（未簽到）"
                        @error="handleImageError('gameBannerImageUrl', $event)"
                        @load="handleImageLoad('gameBannerImageUrl')"
                      />
                      <button
                        type="button"
                        class="btn-remove-image"
                        @click="removeImage('gameBannerImageUrl')"
                      >
                        <i class="ri-close-line"></i>
                      </button>
                    </div>
                    <label class="btn-upload">
                      <input
                        type="file"
                        accept="image/*"
                        @change="handleImageSelect('gameBannerImageUrl', $event)"
                      />
                      <i class="ri-upload-line"></i>
                      {{ visualConfig.gameBannerImageUrl ? '更換圖片' : '上傳圖片' }}
                    </label>
                  </div>
                  <p class="form-hint">未簽到時顯示的「簽到玩遊戲」按鈕圖片</p>
                </div>

                <div class="form-group">
                  <label>簽到玩遊戲按鈕圖片（已簽到）</label>
                  <div class="image-upload-group">
                    <div v-if="visualConfig.gameBannerOkImageUrl" class="image-preview">
                      <img 
                        :src="visualConfig.gameBannerOkImageUrl" 
                        :key="visualConfig.gameBannerOkImageUrl"
                        alt="簽到玩遊戲按鈕圖片（已簽到）"
                        @error="handleImageError('gameBannerOkImageUrl', $event)"
                        @load="handleImageLoad('gameBannerOkImageUrl')"
                      />
                      <button
                        type="button"
                        class="btn-remove-image"
                        @click="removeImage('gameBannerOkImageUrl')"
                      >
                        <i class="ri-close-line"></i>
                      </button>
                    </div>
                    <label class="btn-upload">
                      <input
                        type="file"
                        accept="image/*"
                        @change="handleImageSelect('gameBannerOkImageUrl', $event)"
                      />
                      <i class="ri-upload-line"></i>
                      {{ visualConfig.gameBannerOkImageUrl ? '更換圖片' : '上傳圖片' }}
                    </label>
                  </div>
                  <p class="form-hint">已簽到時顯示的「簽到玩遊戲」按鈕圖片（可選，未設定時使用未簽到圖片）</p>
                </div>
              </div>

              <div class="form-section">
                <h4 class="subsection-title">天數圖片</h4>
                
                <div class="form-group">
                  <label>已簽到天數圖片</label>
                  <div class="image-upload-group">
                    <div v-if="visualConfig.dayOkImageUrl" class="image-preview">
                      <img 
                        :src="visualConfig.dayOkImageUrl" 
                        :key="visualConfig.dayOkImageUrl"
                        alt="已簽到天數圖片"
                        @error="handleImageError('dayOkImageUrl', $event)"
                        @load="handleImageLoad('dayOkImageUrl')"
                      />
                      <button
                        type="button"
                        class="btn-remove-image"
                        @click="removeImage('dayOkImageUrl')"
                      >
                        <i class="ri-close-line"></i>
                      </button>
                    </div>
                    <label class="btn-upload">
                      <input
                        type="file"
                        accept="image/*"
                        @change="handleImageSelect('dayOkImageUrl', $event)"
                      />
                      <i class="ri-upload-line"></i>
                      {{ visualConfig.dayOkImageUrl ? '更換圖片' : '上傳圖片' }}
                    </label>
                  </div>
                  <p class="form-hint">已簽到天數的顯示圖片</p>
                </div>

                <div class="form-group">
                  <label>未簽到天數圖片</label>
                  <div class="image-upload-group">
                    <div v-if="visualConfig.dayGrayImageUrl" class="image-preview">
                      <img 
                        :src="visualConfig.dayGrayImageUrl" 
                        :key="visualConfig.dayGrayImageUrl"
                        alt="未簽到天數圖片"
                        @error="handleImageError('dayGrayImageUrl', $event)"
                        @load="handleImageLoad('dayGrayImageUrl')"
                      />
                      <button
                        type="button"
                        class="btn-remove-image"
                        @click="removeImage('dayGrayImageUrl')"
                      >
                        <i class="ri-close-line"></i>
                      </button>
                    </div>
                    <label class="btn-upload">
                      <input
                        type="file"
                        accept="image/*"
                        @change="handleImageSelect('dayGrayImageUrl', $event)"
                      />
                      <i class="ri-upload-line"></i>
                      {{ visualConfig.dayGrayImageUrl ? '更換圖片' : '上傳圖片' }}
                    </label>
                  </div>
                  <p class="form-hint">未簽到天數的顯示圖片</p>
                </div>

                <div class="form-group">
                  <label>空天數圖片</label>
                  <div class="image-upload-group">
                    <div v-if="visualConfig.dayEmptyImageUrl" class="image-preview">
                      <img 
                        :src="visualConfig.dayEmptyImageUrl" 
                        :key="visualConfig.dayEmptyImageUrl"
                        alt="空天數圖片"
                        @error="handleImageError('dayEmptyImageUrl', $event)"
                        @load="handleImageLoad('dayEmptyImageUrl')"
                      />
                      <button
                        type="button"
                        class="btn-remove-image"
                        @click="removeImage('dayEmptyImageUrl')"
                      >
                        <i class="ri-close-line"></i>
                      </button>
                    </div>
                    <label class="btn-upload">
                      <input
                        type="file"
                        accept="image/*"
                        @change="handleImageSelect('dayEmptyImageUrl', $event)"
                      />
                      <i class="ri-upload-line"></i>
                      {{ visualConfig.dayEmptyImageUrl ? '更換圖片' : '上傳圖片' }}
                    </label>
                  </div>
                  <p class="form-hint">尚未簽到時的顯示圖片（如果沒有設定，會使用已簽到圖片）</p>
                </div>
              </div>

              <div class="form-section">
                <h4 class="subsection-title">歷史記錄圖片</h4>
                
                <div class="form-group">
                  <label>歷史記錄標題圖片</label>
                  <div class="image-upload-group">
                    <div v-if="visualConfig.historyTitleImageUrl" class="image-preview">
                      <img 
                        :src="visualConfig.historyTitleImageUrl" 
                        :key="visualConfig.historyTitleImageUrl"
                        alt="歷史記錄標題圖片"
                        @error="handleImageError('historyTitleImageUrl', $event)"
                        @load="handleImageLoad('historyTitleImageUrl')"
                      />
                      <button
                        type="button"
                        class="btn-remove-image"
                        @click="removeImage('historyTitleImageUrl')"
                      >
                        <i class="ri-close-line"></i>
                      </button>
                    </div>
                    <label class="btn-upload">
                      <input
                        type="file"
                        accept="image/*"
                        @change="handleImageSelect('historyTitleImageUrl', $event)"
                      />
                      <i class="ri-upload-line"></i>
                      {{ visualConfig.historyTitleImageUrl ? '更換圖片' : '上傳圖片' }}
                    </label>
                  </div>
                  <p class="form-hint">歷史記錄區塊的標題圖片</p>
                </div>

                <div class="form-group">
                  <label>歷史記錄圖標</label>
                  <div class="image-upload-group">
                    <div v-if="visualConfig.historyIconImageUrl" class="image-preview">
                      <img 
                        :src="visualConfig.historyIconImageUrl" 
                        :key="visualConfig.historyIconImageUrl"
                        alt="歷史記錄圖標"
                        @error="handleImageError('historyIconImageUrl', $event)"
                        @load="handleImageLoad('historyIconImageUrl')"
                      />
                      <button
                        type="button"
                        class="btn-remove-image"
                        @click="removeImage('historyIconImageUrl')"
                      >
                        <i class="ri-close-line"></i>
                      </button>
                    </div>
                    <label class="btn-upload">
                      <input
                        type="file"
                        accept="image/*"
                        @change="handleImageSelect('historyIconImageUrl', $event)"
                      />
                      <i class="ri-upload-line"></i>
                      {{ visualConfig.historyIconImageUrl ? '更換圖片' : '上傳圖片' }}
                    </label>
                  </div>
                  <p class="form-hint">歷史記錄列表項目的圖標（如果沒有設定，會使用已簽到天數圖片）</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 簽到記錄頁籤 -->
        <div v-if="isEdit && activeTab === 'records'" class="tab-pane">
          <div class="records-section">
            <h3 class="section-title">簽到記錄</h3>
            <div class="filter-group">
              <input
                v-model="recordsFilter.memberSearch"
                type="text"
                class="form-control filter-member-search"
                placeholder="會員姓名/手機/識別碼"
              />
              <div class="date-range">
                <input
                  v-model="recordsFilter.startDate"
                  type="date"
                  class="form-control"
                  placeholder="開始日期"
                />
                <input
                  v-model="recordsFilter.endDate"
                  type="date"
                  class="form-control"
                  placeholder="結束日期"
                />
                <button class="btn btn-primary" @click="fetchRecords">
                  查詢
                </button>
              </div>
            </div>

            <div v-if="loadingRecords" class="loading-state">
              <i class="ri-loader-4-line spin"></i> 載入中...
            </div>

            <div v-else-if="records.length === 0" class="empty-state">
              <p>沒有找到記錄</p>
            </div>

            <div v-else class="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>會員</th>
                    <th>簽到日期</th>
                    <th>總累積天數</th>
                    <th v-if="isConsecutiveCheckinEnabled">連續天數</th>
                    <th style="width: 80px; white-space: nowrap;">操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="record in records" :key="record.id">
                    <td>
                      <span v-if="record.memberName">{{ record.memberName }}</span>
                      <span v-else-if="record.memberPhone">{{ record.memberPhone }}</span>
                      <span v-else-if="record.externalMemberIdentifier">{{ record.externalMemberIdentifier }}</span>
                      <span v-else>-</span>
                    </td>
                    <td>{{ formatDate(record.createdAt) }}</td>
                    <td>{{ record.totalDays }}</td>
                    <td v-if="isConsecutiveCheckinEnabled">{{ record.consecutiveDays }}</td>
                    <td style="white-space: nowrap;">
                      <button
                        class="btn btn-danger btn-sm btn-delete-record"
                        @click="handleDeleteRecord(record.id)"
                        title="刪除記錄"
                      >
                        <i class="ri-delete-bin-line"></i>
                        <span class="btn-text">刪除</span>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- 統計頁籤 -->
        <div v-if="isEdit && activeTab === 'stats'" class="tab-pane">
          <div class="stats-section">
            <h3 class="section-title">簽到統計</h3>
            
            <div class="alert alert-info">
              <i class="ri-information-line"></i>
              <span>提示：如要查看抽獎紀錄，請到 <router-link :to="{ name: 'LotteryConfigList' }" class="alert-link">抽獎工具管理</router-link>。</span>
            </div>

            <div class="filter-group">
              <div class="date-range">
                <input
                  v-model="statsFilter.startDate"
                  type="date"
                  class="form-control"
                  placeholder="開始日期"
                />
                <input
                  v-model="statsFilter.endDate"
                  type="date"
                  class="form-control"
                  placeholder="結束日期"
                />
                <button class="btn btn-primary" @click="fetchStatistics">
                  查詢
                </button>
              </div>
            </div>

            <div v-if="loadingStats" class="loading-state">
              <i class="ri-loader-4-line spin"></i> 載入中...
            </div>

            <div v-else-if="statistics" class="stats-content">
              <div class="stats-summary">
                <div class="stat-card">
                  <h5>總簽到次數</h5>
                  <p class="stat-value">{{ statistics.totalCheckins || 0 }}</p>
                </div>
                <div class="stat-card">
                  <h5>唯一會員數</h5>
                  <p class="stat-value">{{ statistics.uniqueMembers || 0 }}</p>
                </div>
              </div>
              <div class="stats-details">
                <h5>每日統計</h5>
                <div class="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>日期</th>
                        <th>簽到次數</th>
                        <th>唯一會員數</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="daily in statistics.dailyStats" :key="daily.checkinDate">
                        <td>{{ formatDateOnly(daily.checkinDate) }}</td>
                        <td>{{ daily.checkinCount || 0 }}</td>
                        <td>{{ daily.uniqueMembers || 0 }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { checkinService } from '@/api/modules/checkin.js';
import { lotteryService } from '@/api/modules/lottery.js';
import { clientService } from '@/api/modules/clients.js';
import authService from '@/helpers/authservice/authService.js';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';
import TagSelectorMultiple from '@/components/TagSelectorMultiple.vue';
import LineMsgTemplatePicker from '@/components/LineMsgTemplatePicker.vue';
import MemberSourceSelector from '@/components/MemberSourceSelector.vue';
import WixtarTicketMultiSelect from '@/components/WixtarTicketMultiSelect.vue';
import RewardStockWarning from '@/components/RewardStockWarning.vue';
import { couponService } from '@/api/modules/coupons.js';
import { wixtarService } from '@/api/modules/wixtar.js';
import Swal from 'sweetalert2';

export default {
  name: 'CreateCheckinConfig',
  components: {
    TagSelectorMultiple,
    LineMsgTemplatePicker,
    MemberSourceSelector,
    WixtarTicketMultiSelect,
    RewardStockWarning
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const isEdit = computed(() => !!route.params?.id);
    const configId = computed(() => route.params?.id || '');
    const loading = ref(false);
    const submitting = ref(false);
    const activeTab = ref('basic');

    const form = reactive({
      configKey: '',
      configName: '',
      description: '',
      isActive: true,
      dailyCheckinEnabled: true,
      dailyCheckinLotteryConfigId: null,
      cumulativeEnabled: false,
      cumulativeType: 'total',
      resetType: 'never',
      startDate: '',
      endDate: '',
      memberSource: 'LOCKLINE'
    });

    const errors = reactive({
      configName: '',
      isActive: '',
      checkinEnabled: '',
      startDate: '',
      endDate: ''
    });

    // 獎勵管理相關
    const rewards = ref([]);
    const loadingRewards = ref(false);
    const lotteryConfigs = ref([]);
    const showRewardModal = ref(false);
    const editingReward = ref(null);
    const submittingReward = ref(false);
    const rewardFormPresetDays = ref(null);
    const couponTemplates = ref([]);
    const lineMsgTemplate = ref(null);
    const rewardForm = reactive({
      days: 1,
      rewardType: 'point',
      lotteryConfigId: null,
      rewardData: {},
      isActive: true
    });

    // 記錄查詢相關
    const records = ref([]);
    const loadingRecords = ref(false);
    const recordsFilter = reactive({
      memberSearch: '',
      startDate: '',
      endDate: ''
    });

    // 統計查詢相關
    const statistics = ref(null);
    const loadingStats = ref(false);
    const statsFilter = reactive({
      startDate: '',
      endDate: ''
    });

    // 視覺配置相關
    const visualConfig = ref({});
    const loadingVisualConfig = ref(false);
    const savingVisualConfig = ref(false);
    
    // 標記是否已完成初始化（用於 watch 判斷是否為用戶主動切換）
    const isInitialized = ref(false);

    // 客戶資訊相關
    const clientMemberSystemType = ref(null);
    const loadingClientInfo = ref(false);
    const wixtarTickets = ref([]);
    const wixtarTicketsLoading = ref(false);
    const isWixtarClient = computed(() => clientMemberSystemType.value === 'WIXTAR');

    const loadWixtarTickets = async () => {
      if (!isWixtarClient.value) return;
      wixtarTicketsLoading.value = true;
      try {
        wixtarTickets.value = await wixtarService.listAllTicketCatalog();
      } catch (error) {
        console.error('載入 Wixtar 票券失敗:', error);
        wixtarTickets.value = [];
      } finally {
        wixtarTicketsLoading.value = false;
      }
    };

    // 載入客戶資訊
    const loadClientInfo = async () => {
      const currentUser = authService.getCurrentUser();
      if (!currentUser?.clientId) {
        return;
      }

      loadingClientInfo.value = true;
      try {
        const response = await clientService.getClient(currentUser.clientId);
        if (response && response.success && response.data?.clientData) {
          clientMemberSystemType.value = response.data.clientData.memberSystemType || 'LOCKLINE';
        }
        await loadWixtarTickets();
      } catch (error) {
        console.error('載入客戶資訊失敗:', error);
      } finally {
        loadingClientInfo.value = false;
      }
    };

    // 檢查會員來源與抽獎配置是否匹配的共用函數
    const checkMemberSourceMismatch = async (memberSource) => {
      if (!isEdit.value) {
        return;
      }

      // 等待抽獎配置列表載入完成
      if (lotteryConfigs.value.length === 0) {
        await fetchLotteryConfigs();
      }

      const mismatchedConfigs = [];

      // 檢查每日簽到的抽獎配置
      if (form.dailyCheckinLotteryConfigId) {
        const isValid = isLotteryConfigValid(form.dailyCheckinLotteryConfigId);
        console.log('🔍 檢查每日簽到抽獎配置:', {
          lotteryConfigId: form.dailyCheckinLotteryConfigId,
          isValid,
          checkinMemberSource: memberSource
        });
        
        if (!isValid) {
          const lottery = lotteryConfigs.value.find(l => l.id === form.dailyCheckinLotteryConfigId);
          if (lottery) {
            mismatchedConfigs.push({
              type: '每日簽到',
              name: lottery.configName,
              currentSource: lottery.memberSource || 'LOCKLINE',
              requiredSource: memberSource
            });
          }
        }
      }

      // 檢查累積簽到獎勵中的抽獎配置
      rewards.value.forEach(reward => {
        if (reward.rewardType === 'lottery' && reward.lotteryConfigId) {
          const isValid = isLotteryConfigValid(reward.lotteryConfigId);
          console.log('🔍 檢查累積簽到獎勵:', {
            days: reward.days,
            lotteryConfigId: reward.lotteryConfigId,
            isValid
          });
          
          if (!isValid) {
            const lottery = lotteryConfigs.value.find(l => l.id === reward.lotteryConfigId);
            if (lottery) {
              mismatchedConfigs.push({
                type: `累積簽到 ${reward.days} 天獎勵`,
                name: lottery.configName,
                currentSource: lottery.memberSource || 'LOCKLINE',
                requiredSource: memberSource
              });
            }
          }
        }
      });

      console.log('🔍 不匹配的配置:', mismatchedConfigs);

      // 如果有不匹配的配置，顯示警告
      if (mismatchedConfigs.length > 0) {
        const configList = mismatchedConfigs.map(c => 
          `• ${c.type}：${c.name}（目前為 ${c.currentSource === 'WIXTAR' ? 'Wixtar' : c.currentSource === 'EXTERNAL' ? '外部會員' : 'LOCKLINE'} 會員）`
        ).join('\n');

        const sourceText = memberSource === 'WIXTAR' ? 'Wixtar' : memberSource === 'EXTERNAL' ? '外部會員' : 'LOCKLINE';
        
        await Swal.fire({
          icon: 'warning',
          title: '會員來源不匹配',
          html: `
            <p>此簽到活動設定為 <strong>${sourceText} 會員</strong>，但以下抽獎配置不匹配：</p>
            <pre style="text-align: left; background: #f5f5f5; padding: 10px; border-radius: 4px; white-space: pre-wrap;">${configList}</pre>
            <p style="margin-top: 10px;">請前往「獎勵管理」頁籤修改或移除這些不匹配的配置。</p>
          `,
          confirmButtonText: '我知道了',
          width: '600px'
        });
      }
    };

    // 載入編輯資料
    const loadConfig = async () => {
      if (!isEdit.value) {
        // 新增模式下，標記為已初始化（因為沒有數據需要載入）
        isInitialized.value = true;
        return;
      }

      loading.value = true;
      try {
        const response = await checkinService.getConfigById(configId.value);
        if (response && response.success && response.data) {
          const config = response.data;
          
          form.configKey = config.configKey || '';
          form.configName = config.configName || '';
          
          // 直接使用 description（純文字或 HTML 都可以）
          form.description = config.description || '';
          
          console.log('📥 載入簽到配置 - description:', {
            configId: configId.value,
            description: config.description ? `${config.description.substring(0, 80)}...` : 'empty',
            length: config.description ? config.description.length : 0
          });
          
          form.isActive = config.isActive !== undefined ? config.isActive : true;
          form.dailyCheckinEnabled = config.dailyCheckinEnabled !== undefined ? config.dailyCheckinEnabled : true;
          form.dailyCheckinLotteryConfigId = config.dailyCheckinLotteryConfigId || null;
          form.cumulativeEnabled = config.cumulativeEnabled !== undefined ? config.cumulativeEnabled : false;
          form.cumulativeType = config.cumulativeType || 'total';
          form.resetType = config.resetType || 'never';
          // 優先使用 memberSource
          form.memberSource = config.memberSource || 'LOCKLINE';
          // 處理日期時間欄位（轉換為 datetime-local 格式）
          form.startDate = config.startDate ? formatDateTimeLocal(config.startDate) : '';
          form.endDate = config.endDate ? formatDateTimeLocal(config.endDate) : '';
          
          // 載入抽獎配置列表、獎勵列表和視覺配置
          await Promise.all([fetchLotteryConfigs(), fetchRewards(), loadVisualConfig()]);
          
          // 標記初始化完成（在數據載入完成後）
          isInitialized.value = true;
          
          // 載入完成後，檢查抽獎配置是否匹配（如果會員來源已設定）
          if (form.memberSource) {
            await checkMemberSourceMismatch(form.memberSource);
          }
        } else {
          await showError('載入失敗', response?.message || '無法載入簽到配置');
          router.push({ name: 'CheckinConfigList' });
        }
      } catch (err) {
        console.error('載入簽到配置失敗:', err);
        await showError('載入失敗', err.message || '無法載入簽到配置');
        router.push({ name: 'CheckinConfigList' });
      } finally {
        loading.value = false;
        // 即使載入失敗，也標記為已初始化（避免後續操作時一直不觸發）
        if (isEdit.value) {
          isInitialized.value = true;
        }
      }
    };

    // 載入抽獎配置列表
    const fetchLotteryConfigs = async () => {
      try {
        const response = await lotteryService.getConfigs({ isActive: true, pageSize: 100 });
        if (response && response.success && response.data) {
          lotteryConfigs.value = response.data.items || [];
          // 調試：檢查抽獎配置的 memberSource 字段
          console.log('📋 載入的抽獎配置列表:', lotteryConfigs.value.map(l => ({
            id: l.id,
            configName: l.configName,
            memberSource: l.memberSource,
            member_source: l.member_source // 檢查是否有下劃線版本
          })));
        }
      } catch (err) {
        console.error('獲取抽獎配置列表失敗:', err);
      }
    };

    // 載入獎勵列表
    const fetchRewards = async () => {
      if (!isEdit.value) return;

      loadingRewards.value = true;
      try {
        const response = await checkinService.getRewards(configId.value);
        if (response && response.success && response.data) {
          rewards.value = response.data || [];
        }
      } catch (err) {
        console.error('獲取獎勵列表失敗:', err);
        await showError('載入失敗', err.message || '獲取獎勵列表失敗');
      } finally {
        loadingRewards.value = false;
      }
    };

    // 載入簽到記錄
    const fetchRecords = async () => {
      if (!isEdit.value) return;

      loadingRecords.value = true;
      try {
        const params = {
          configId: configId.value,
          page: 1,
          pageSize: 50
        };

        if (recordsFilter.memberSearch) {
          params.memberSearch = recordsFilter.memberSearch.trim();
        }

        if (recordsFilter.startDate) {
          params.startDate = recordsFilter.startDate;
        }

        if (recordsFilter.endDate) {
          params.endDate = recordsFilter.endDate;
        }

        const response = await checkinService.getRecords(params);
        if (response && response.success && response.data) {
          records.value = response.data.items || [];
        }
      } catch (err) {
        console.error('獲取簽到記錄失敗:', err);
        await showError('載入失敗', err.message || '獲取簽到記錄失敗');
      } finally {
        loadingRecords.value = false;
      }
    };

    // 載入統計
    const fetchStatistics = async () => {
      if (!isEdit.value) return;

      loadingStats.value = true;
      try {
        const params = {};
        if (statsFilter.startDate) {
          params.startDate = statsFilter.startDate;
        }
        if (statsFilter.endDate) {
          params.endDate = statsFilter.endDate;
        }

        const response = await checkinService.getStatistics(configId.value, params);
        if (response && response.success && response.data) {
          statistics.value = response.data;
        }
      } catch (err) {
        console.error('獲取統計失敗:', err);
        await showError('載入失敗', err.message || '獲取統計失敗');
      } finally {
        loadingStats.value = false;
      }
    };

    // 刪除簽到記錄
    const handleDeleteRecord = async (recordId) => {
      const confirmed = await showDeleteConfirm('確定要刪除此簽到記錄嗎？此操作無法復原。');
      if (!confirmed) {
        return;
      }

      try {
        await checkinService.deleteRecord(recordId);
        await showSuccess('刪除成功');
        // 重新載入記錄列表
        await fetchRecords();
      } catch (err) {
        console.error('刪除簽到記錄失敗:', err);
        await showError('刪除失敗', err.message || '刪除簽到記錄失敗');
      }
    };

    // 格式化日期時間為 datetime-local 格式
    const formatDateTimeLocal = (dateTimeString) => {
      if (!dateTimeString) return '';
      const date = new Date(dateTimeString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    // 驗證表單
    const validateForm = () => {
      Object.keys(errors).forEach(key => {
        errors[key] = '';
      });

      let isValid = true;

      if (!form.configName || form.configName.trim() === '') {
        errors.configName = '簽到活動名稱為必填欄位';
        isValid = false;
      }

      // 驗證：每日簽到和累積簽到至少要有一個啟用
      if (!form.dailyCheckinEnabled && !form.cumulativeEnabled) {
        errors.checkinEnabled = '每日簽到和累積簽到至少要有一個啟用';
        isValid = false;
      }

      // 驗證：結束時間必須晚於開始時間
      if (form.startDate && form.endDate) {
        const start = new Date(form.startDate);
        const end = new Date(form.endDate);
        if (end <= start) {
          errors.endDate = '活動結束時間必須晚於開始時間';
          isValid = false;
        }
      }

      return isValid;
    };

    // 提交表單
    const handleSubmit = async () => {
      if (!validateForm()) {
        return;
      }

      submitting.value = true;

      try {
        if (isEdit.value) {
          const updateData = {
            configName: form.configName,
            description: form.description ? form.description.trim() : null,
            isActive: form.isActive,
            dailyCheckinEnabled: form.dailyCheckinEnabled,
            dailyCheckinLotteryConfigId: form.dailyCheckinLotteryConfigId,
            cumulativeEnabled: form.cumulativeEnabled,
            cumulativeType: form.cumulativeType,
            resetType: form.resetType,
            startDate: form.startDate ? new Date(form.startDate).toISOString() : null,
            endDate: form.endDate ? new Date(form.endDate).toISOString() : null,
            // 只發送 memberSource，不再發送舊的 allowExternalMember 和 enableWixtarMember
            // 後端會根據 memberSource 自動處理舊字段的同步
            memberSource: form.memberSource
          };

          console.log('📝 準備更新簽到配置:', {
            configId: configId.value,
            description: updateData.description ? `${updateData.description.substring(0, 50)}...` : 'null',
            descriptionLength: updateData.description ? updateData.description.length : 0
          });

          console.log('📤 發送更新請求:', {
            configId: configId.value,
            updateData: {
              ...updateData,
              description: updateData.description ? `${updateData.description.substring(0, 50)}...` : 'null'
            }
          });

          const response = await checkinService.updateConfig(configId.value, updateData);

          console.log('📥 收到更新回應:', {
            success: response?.success,
            data: response?.data ? {
              ...response.data,
              description: response.data.description ? `${response.data.description.substring(0, 50)}...` : 'null'
            } : null
          });

          if (response && response.success) {
            await showSuccess('更新成功', '簽到配置已更新');
            // 重新載入配置以確保顯示最新資料
            await loadConfig();
          } else {
            await showError('更新失敗', response?.message || '更新簽到配置失敗');
          }
        } else {
          const createData = { ...form };
          createData.description = form.description ? form.description.trim() : null;
          if (!createData.configKey || createData.configKey.trim() === '') {
            delete createData.configKey;
          }
          // 轉換日期時間格式
          createData.startDate = form.startDate ? new Date(form.startDate).toISOString() : null;
          createData.endDate = form.endDate ? new Date(form.endDate).toISOString() : null;

          console.log('📝 準備建立簽到配置:', {
            description: createData.description ? `${createData.description.substring(0, 50)}...` : 'null',
            descriptionLength: createData.description ? createData.description.length : 0
          });

          const response = await checkinService.createConfig(createData);

          if (response && response.success) {
            await showSuccess('建立成功', '簽到配置已建立');
            // 建立成功後跳轉到編輯頁面
            router.push({ name: 'EditCheckinConfig', params: { id: response.data.id } });
          } else {
            await showError('建立失敗', response?.message || '建立簽到配置失敗');
          }
        }
      } catch (err) {
        console.error('儲存簽到配置失敗:', err);
        const errorMessage = err.response?.data?.message || err.message || '儲存簽到配置失敗';
        await showError('儲存失敗', errorMessage);
      } finally {
        submitting.value = false;
      }
    };

    const handleCancel = () => {
      router.push({ name: 'CheckinConfigList' });
    };

    // 每日簽到獎勵變更處理
    const handleDailyRewardChange = async () => {
      if (!isEdit.value) return;
      
      // 驗證抽獎配置的會員來源是否與簽到配置匹配
      if (form.dailyCheckinLotteryConfigId && !isLotteryConfigValid(form.dailyCheckinLotteryConfigId)) {
        const selectedLottery = lotteryConfigs.value.find(l => l.id === form.dailyCheckinLotteryConfigId);
        if (selectedLottery) {
          if (form.memberSource === 'WIXTAR') {
            await showError('驗證失敗', '此簽到活動僅允許 Wixtar 會員，因此只能選擇 Wixtar 會員的抽獎配置');
          } else if (form.memberSource === 'LOCKLINE') {
            await showError('驗證失敗', '此簽到活動僅允許 LOCKLINE 會員，因此只能選擇 LOCKLINE 會員的抽獎配置');
          } else if (form.memberSource === 'EXTERNAL') {
            await showError('驗證失敗', '此簽到活動允許外部會員，因此只能選擇外部會員的抽獎配置');
          }
          // 重置選擇
          form.dailyCheckinLotteryConfigId = null;
          return;
        }
      }
      
      try {
        const updateData = {
          dailyCheckinLotteryConfigId: form.dailyCheckinLotteryConfigId
        };
        await checkinService.updateConfig(configId.value, updateData);
        await showSuccess('更新成功', '每日簽到獎勵已更新');
      } catch (err) {
        console.error('更新每日簽到獎勵失敗:', err);
        await showError('更新失敗', err.message || '更新每日簽到獎勵失敗');
      }
    };

    const resetRewardForm = (days = 1) => {
      rewardForm.days = days;
      rewardForm.rewardType = 'point';
      rewardForm.lotteryConfigId = null;
      rewardForm.rewardData = {};
      rewardForm.isActive = true;
      lineMsgTemplate.value = null;
    };

    // 新增獎勵
    const handleAddReward = () => {
      editingReward.value = null;
      rewardFormPresetDays.value = null;
      resetRewardForm(1);
      showRewardModal.value = true;
    };

    const handleAddRewardForDays = (days) => {
      editingReward.value = null;
      rewardFormPresetDays.value = days;
      resetRewardForm(days);
      showRewardModal.value = true;
    };

    // 編輯獎勵
    const handleEditReward = (reward) => {
      editingReward.value = reward;
      rewardFormPresetDays.value = null;
      rewardForm.days = reward.days;
      rewardForm.rewardType = reward.rewardType || 'lottery';
      rewardForm.lotteryConfigId = reward.lotteryConfigId || null;
      if (reward.rewardType === 'tag') {
        rewardForm.rewardData = { tagIds: reward.rewardData?.tagIds || [] };
        lineMsgTemplate.value = null;
      } else if (reward.rewardType === 'message') {
        rewardForm.rewardData = {
          templateCode: reward.rewardData?.templateCode || '',
          description: reward.rewardData?.description || ''
        };
        lineMsgTemplate.value = null;
      } else if (reward.rewardType === 'wixtar_ticket') {
        rewardForm.rewardData = { wixtarTicketIds: reward.rewardData?.wixtarTicketIds || [] };
        lineMsgTemplate.value = null;
      } else {
        rewardForm.rewardData = reward.rewardData || {};
        lineMsgTemplate.value = null;
      }
      rewardForm.isActive = reward.isActive !== undefined ? reward.isActive : true;
      showRewardModal.value = true;
    };

    // 關閉獎勵 Modal
    const closeRewardModal = () => {
      showRewardModal.value = false;
      editingReward.value = null;
      rewardFormPresetDays.value = null;
      resetRewardForm(1);
    };

    const onMessageTemplateChange = (template) => {
      lineMsgTemplate.value = template;
      rewardForm.rewardData.templateCode = template ? (template.templateCode || template.id) : '';
    };

    // 獎勵類型變更處理
    const handleRewardTypeChange = () => {
      if (rewardForm.rewardType === 'tag') {
        rewardForm.rewardData = { tagIds: [] };
      } else if (rewardForm.rewardType === 'message') {
        rewardForm.rewardData = { templateCode: '', description: '' };
        lineMsgTemplate.value = null;
      } else if (rewardForm.rewardType === 'wixtar_ticket') {
        rewardForm.rewardData = { wixtarTicketIds: [] };
        lineMsgTemplate.value = null;
      } else {
        rewardForm.rewardData = {};
      }
      rewardForm.lotteryConfigId = null;
    };

    // 提交獎勵
    const handleSubmitReward = async () => {
      // 驗證
      if (!rewardForm.days || rewardForm.days <= 0) {
        await showError('驗證失敗', '達成天數必須大於 0');
        return;
      }

      if (rewardForm.rewardType === 'lottery' && !rewardForm.lotteryConfigId) {
        await showError('驗證失敗', '請選擇抽獎配置');
        return;
      }

      if (rewardForm.rewardType === 'point' && (!rewardForm.rewardData.points || rewardForm.rewardData.points <= 0)) {
        await showError('驗證失敗', '點數必須大於 0');
        return;
      }

      if (rewardForm.rewardType === 'coupon' && !rewardForm.rewardData.couponId) {
        await showError('驗證失敗', '請輸入優惠券 ID');
        return;
      }

      if (rewardForm.rewardType === 'tag') {
        const tagIds = rewardForm.rewardData?.tagIds || [];
        if (!tagIds.length) {
          await showError('驗證失敗', '請至少選擇一個標籤');
          return;
        }
      }

      if (rewardForm.rewardType === 'message') {
        const templateCode = lineMsgTemplate.value
          ? (lineMsgTemplate.value.templateCode || lineMsgTemplate.value.id)
          : (rewardForm.rewardData?.templateCode || '');
        if (!templateCode) {
          await showError('驗證失敗', '請選擇 LINE 訊息範本');
          return;
        }
      }

      if (rewardForm.rewardType === 'wixtar_ticket' && !(rewardForm.rewardData?.wixtarTicketIds || []).length) {
        await showError('驗證失敗', '請至少選擇一張 Wixtar 票券');
        return;
      }

      // 驗證抽獎配置的會員來源是否與簽到配置匹配
      if (rewardForm.rewardType === 'lottery' && rewardForm.lotteryConfigId) {
        const selectedLottery = lotteryConfigs.value.find(l => l.id === rewardForm.lotteryConfigId);
        if (selectedLottery) {
          const checkinMemberSource = form.memberSource || 'LOCKLINE';
          const lotteryMemberSource = selectedLottery.memberSource || 'LOCKLINE';
          
          // 如果簽到配置是 WIXTAR，但抽獎配置不是 WIXTAR，顯示錯誤
          if (checkinMemberSource === 'WIXTAR' && lotteryMemberSource !== 'WIXTAR') {
            await showError('驗證失敗', '此簽到活動僅允許 Wixtar 會員，因此只能選擇 Wixtar 會員的抽獎配置');
            return;
          }
          
          // 如果簽到配置是 LOCKLINE，但抽獎配置不是 LOCKLINE，顯示錯誤
          if (checkinMemberSource === 'LOCKLINE' && lotteryMemberSource !== 'LOCKLINE') {
            await showError('驗證失敗', '此簽到活動僅允許 LOCKLINE 會員，因此只能選擇 LOCKLINE 會員的抽獎配置');
            return;
          }
          
          // 如果簽到配置是 EXTERNAL，但抽獎配置不是 EXTERNAL，顯示錯誤
          if (checkinMemberSource === 'EXTERNAL' && lotteryMemberSource !== 'EXTERNAL') {
            await showError('驗證失敗', '此簽到活動允許外部會員，因此只能選擇外部會員的抽獎配置');
            return;
          }
        }
      }

      submittingReward.value = true;

      try {
        const rewardData = {
          days: rewardForm.days,
          rewardType: rewardForm.rewardType,
          lotteryConfigId: rewardForm.rewardType === 'lottery' ? rewardForm.lotteryConfigId : null,
          rewardData: rewardForm.rewardType === 'lottery'
            ? null
            : rewardForm.rewardType === 'message'
              ? {
                  templateCode: lineMsgTemplate.value
                    ? (lineMsgTemplate.value.templateCode || lineMsgTemplate.value.id)
                    : rewardForm.rewardData.templateCode,
                  description: rewardForm.rewardData?.description || null
                }
              : rewardForm.rewardData,
          isActive: rewardForm.isActive
        };

        if (editingReward.value) {
          // 更新
          const response = await checkinService.updateReward(editingReward.value.id, rewardData);
          if (response && response.success) {
            await showSuccess('更新成功', '目標已更新');
            closeRewardModal();
            await fetchRewards();
          } else {
            await showError('更新失敗', response?.message || '更新目標失敗');
          }
        } else {
          // 新增
          const response = await checkinService.createReward(configId.value, rewardData);
          if (response && response.success) {
            await showSuccess('建立成功', '目標已建立');
            closeRewardModal();
            await fetchRewards();
          } else {
            await showError('建立失敗', response?.message || '建立目標失敗');
          }
        }
      } catch (err) {
        console.error('儲存目標失敗:', err);
        const errorMessage = err.response?.data?.message || err.message || '儲存目標失敗';
        await showError('儲存失敗', errorMessage);
      } finally {
        submittingReward.value = false;
      }
    };

    const handleDeleteReward = async (rewardId) => {
      try {
        const confirmed = await showDeleteConfirm('此目標');
        if (!confirmed) return;

        const response = await checkinService.deleteReward(rewardId);
        if (response && response.success) {
          await showSuccess('刪除成功', '目標已刪除');
          await fetchRewards();
        }
      } catch (err) {
        await showError('刪除失敗', err.message);
      }
    };

    const getRewardTypeText = (rewardType) => {
      const typeMap = {
        lottery: '抽獎',
        point: '點數',
        coupon: '優惠券',
        tag: '貼標',
        message: '發送訊息',
        wixtar_ticket: 'Wixtar 票券',
        other: '其他'
      };
      return typeMap[rewardType] || rewardType;
    };

    // 驗證抽獎配置的會員來源是否與簽到配置匹配
    const isLotteryConfigValid = (lotteryConfigId) => {
      if (!lotteryConfigId) return true; // 未選擇時不驗證
      
      const checkinMemberSource = form.memberSource || 'LOCKLINE';
      const lottery = lotteryConfigs.value.find(l => l.id === lotteryConfigId);
      
      if (!lottery) {
        console.warn('⚠️ 找不到抽獎配置:', lotteryConfigId);
        return true; // 找不到配置時不驗證
      }
      
      // 支援兩種字段名：memberSource (駝峰) 和 member_source (下劃線)
      const lotteryMemberSource = lottery.memberSource || lottery.member_source || 'LOCKLINE';
      
      // 調試：記錄驗證過程
      console.log('🔍 驗證抽獎配置:', {
        lotteryConfigId,
        lotteryConfigName: lottery.configName,
        checkinMemberSource,
        lotteryMemberSource,
        lotteryRaw: {
          memberSource: lottery.memberSource,
          member_source: lottery.member_source
        },
        isValid: checkinMemberSource === lotteryMemberSource
      });
      
      // 如果簽到配置是 WIXTAR，抽獎配置也必須是 WIXTAR
      if (checkinMemberSource === 'WIXTAR') {
        return lotteryMemberSource === 'WIXTAR';
      }
      
      // 如果簽到配置是 LOCKLINE，抽獎配置也必須是 LOCKLINE
      if (checkinMemberSource === 'LOCKLINE') {
        return lotteryMemberSource === 'LOCKLINE';
      }
      
      // 如果簽到配置是 EXTERNAL，抽獎配置也必須是 EXTERNAL
      if (checkinMemberSource === 'EXTERNAL') {
        return lotteryMemberSource === 'EXTERNAL';
      }
      
      return true;
    };

    const getRewardContent = (reward) => {
      if (reward.rewardType === 'lottery') {
        const lottery = lotteryConfigs.value.find(l => l.id === reward.lotteryConfigId);
        return lottery ? lottery.configName : '-';
      } else if (reward.rewardType === 'point') {
        return `${reward.rewardData?.points || 0} 點`;
      } else if (reward.rewardType === 'coupon') {
        return reward.rewardData?.couponId || '-';
      } else if (reward.rewardType === 'tag') {
        const count = reward.rewardData?.tagIds?.length || 0;
        return count ? `${count} 個標籤` : '-';
      } else if (reward.rewardType === 'message') {
        return reward.rewardData?.templateCode ? '已設定訊息範本' : '-';
      } else if (reward.rewardType === 'wixtar_ticket') {
        const count = reward.rewardData?.wixtarTicketIds?.length || 0;
        return count ? `${count} 張 Wixtar 票券` : '-';
      } else if (reward.rewardType === 'other') {
        return reward.rewardData?.description || '-';
      }
      return '-';
    };

    // 排序獎勵（按天數與顯示順序）
    const sortedRewards = computed(() => {
      return [...rewards.value].sort((a, b) => {
        if (a.days !== b.days) return a.days - b.days;
        return (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0);
      });
    });

    const groupedRewardList = computed(() => {
      const groups = [];
      const groupMap = new Map();

      sortedRewards.value.forEach((reward) => {
        if (!groupMap.has(reward.days)) {
          const group = { days: reward.days, rewards: [] };
          groupMap.set(reward.days, group);
          groups.push(group);
        }
        groupMap.get(reward.days).rewards.push(reward);
      });

      return groups;
    });

    // 計算屬性：是否啟用連續簽到
    const isConsecutiveCheckinEnabled = computed(() => {
      return form.cumulativeEnabled === true && form.cumulativeType === 'consecutive';
    });

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      const y = date.getFullYear();
      const M = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const h = String(date.getHours()).padStart(2, '0');
      const m = String(date.getMinutes()).padStart(2, '0');
      return `${y}-${M}-${d} ${h}:${m}`;
    };

    const formatDateOnly = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      const y = date.getFullYear();
      const M = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${M}-${d}`;
    };

    // 獲取 Lottery 前端基礎 URL 的輔助函數
    const getLotteryBaseUrl = () => {
      // 優先使用環境變數
      if (process.env.VUE_APP_LOTTERY_BASE_URL) {
        return process.env.VUE_APP_LOTTERY_BASE_URL;
      }
      
      // 開發環境
      if (process.env.NODE_ENV === 'development') {
        return 'http://localhost:8081';
      }
      
      // 生產環境：根據當前域名判斷
      const origin = window.location.origin;
      
      // 如果當前是 admin.lockline.io，應該指向 app.lockline.io
      if (origin.includes('admin.lockline.io')) {
        return origin.replace('admin.lockline.io', 'app.lockline.io');
      }
      
      // 如果當前是 localhost 或其他環境，嘗試替換端口
      if (origin.includes('localhost') || origin.match(/:\d+$/)) {
        return origin.replace(/:\d+$/, ':8081');
      }
      
      // 預設使用 app.lockline.io（生產環境）
      return 'https://app.lockline.io';
    };

    // 計算路由 URL
    const checkinRouteUrl = computed(() => {
      if (!isEdit.value || !configId.value) return '';
      const lotteryBaseUrl = getLotteryBaseUrl();
      
      // 根據會員來源來決定參數名稱
      const paramName = form.memberSource === 'WIXTAR' ? 'p' : 'm';
      const paramValue = form.memberSource === 'WIXTAR' ? 'phoneNumber' : 'memberId';
      
      return `${lotteryBaseUrl}/lottery/checkin/${configId.value}?${paramName}=${paramValue}`;
    });

    // 計算測試模式路由 URL
    const checkinTestModeUrl = computed(() => {
      if (!isEdit.value || !configId.value) return '';
      const lotteryBaseUrl = getLotteryBaseUrl();
      
      // 根據會員來源來決定參數名稱
      const paramName = form.memberSource === 'WIXTAR' ? 'p' : 'm';
      const paramValue = form.memberSource === 'WIXTAR' ? 'phoneNumber' : 'memberId';
      
      return `${lotteryBaseUrl}/lottery/checkin/${configId.value}?${paramName}=${paramValue}&testMode=1`;
    });

    const ruleRouteUrl = computed(() => {
      if (!isEdit.value || !configId.value) return '';
      const lotteryBaseUrl = getLotteryBaseUrl();
      
      // 根據會員來源來決定參數名稱
      const paramName = form.memberSource === 'WIXTAR' ? 'p' : 'm';
      const paramValue = form.memberSource === 'WIXTAR' ? 'phoneNumber' : 'memberId';
      
      return `${lotteryBaseUrl}/lottery/rule/${configId.value}?type=checkin&${paramName}=${paramValue}`;
    });

    // 計算測試模式規則路由 URL
    const ruleTestModeUrl = computed(() => {
      if (!isEdit.value || !configId.value) return '';
      const lotteryBaseUrl = getLotteryBaseUrl();
      
      // 根據會員來源來決定參數名稱
      const paramName = form.memberSource === 'WIXTAR' ? 'p' : 'm';
      const paramValue = form.memberSource === 'WIXTAR' ? 'phoneNumber' : 'memberId';
      
      return `${lotteryBaseUrl}/lottery/rule/${configId.value}?type=checkin&${paramName}=${paramValue}&testMode=1`;
    });

    // 複製到剪貼板
    const copyToClipboard = async (text, label = '連結') => {
      try {
        await navigator.clipboard.writeText(text);
        await showSuccess('複製成功', `${label}連結已複製到剪貼板`);
      } catch (err) {
        console.error('複製失敗:', err);
        // 降級方案：使用傳統方法
        try {
          const textArea = document.createElement('textarea');
          textArea.value = text;
          textArea.style.position = 'fixed';
          textArea.style.opacity = '0';
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          await showSuccess('複製成功', `${label}連結已複製到剪貼板`);
        } catch (fallbackErr) {
          console.error('降級複製也失敗:', fallbackErr);
          await showError('複製失敗', '無法複製到剪貼板，請手動複製');
        }
      }
    };

    // 監聽頁籤切換
    watch(activeTab, (newTab) => {
      if (isEdit.value) {
        if (newTab === 'rewards') {
          fetchRewards();
        } else if (newTab === 'records') {
          fetchRecords();
        } else if (newTab === 'stats') {
          fetchStatistics();
        }
      }
    });

    // 載入視覺配置
    const loadVisualConfig = async () => {
      if (!isEdit.value) return;

      loadingVisualConfig.value = true;
      try {
        const response = await checkinService.getVisualConfig(configId.value);
        if (response && response.success && response.data) {
          const config = response.data.visualConfig || {};
          
          // 處理圖片 URL：
          // - S3 URL 直接使用（已經是完整 URL）
          // - 完整的 HTTP/HTTPS URL 直接使用
          // - 舊的 /uploads/ 路徑需要轉換為完整 URL（向後兼容）
          const imageFields = [
            'backgroundImageUrl',
            'backgroundImageMaskUrl',
            'ruleButtonImageUrl',
            'dayOkImageUrl',
            'dayGrayImageUrl',
            'dayEmptyImageUrl',
            'gameBannerImageUrl',
            'gameBannerOkImageUrl',
            'historyTitleImageUrl',
            'historyIconImageUrl'
          ];
          
          imageFields.forEach(field => {
            if (config[field] && typeof config[field] === 'string') {
              const url = config[field];
              
              // 如果是完整的 HTTP/HTTPS URL（包括 S3 URL），直接使用
              if (url.startsWith('http://') || url.startsWith('https://')) {
                // 已經是完整的 URL，不需要轉換
                return;
              }
              
              // 如果是舊的 /uploads/ 路徑，轉換為完整 URL（向後兼容）
              if (url.startsWith('/uploads/')) {
                if (process.env.NODE_ENV === 'development') {
                  config[field] = `http://localhost:3000${url}`;
                } else {
                  config[field] = `${window.location.origin}${url}`;
                }
              }
            }
          });
          
          // 設定顏色欄位的預設值（如果不存在）
          if (!config.backgroundColor) {
            config.backgroundColor = '#f3f3f3';
          }
          if (!config.borderColor) {
            config.borderColor = '#000000';
          }
          if (!config.messageButtonTextColor) {
            config.messageButtonTextColor = '#ffffff';
          }
          if (!config.messageButtonBackgroundColor) {
            config.messageButtonBackgroundColor = '#007bff';
          }
          
          visualConfig.value = config;
        }
      } catch (err) {
        console.error('載入視覺配置失敗:', err);
      } finally {
        loadingVisualConfig.value = false;
      }
    };

    // 儲存視覺配置
    const saveVisualConfig = async (reload = true) => {
      if (!isEdit.value) return;

      savingVisualConfig.value = true;
      try {
        // 準備保存的配置
        // 現在使用 S3，保存完整的 S3 URL，不需要轉換
        const configToSave = JSON.parse(JSON.stringify(visualConfig.value));
        
        // 保留所有圖片 URL 為完整 URL（S3 URL 或舊的完整 URL）
        // 不需要轉換，直接保存即可
        
        const response = await checkinService.updateVisualConfig(configId.value, configToSave);
        if (response && response.success) {
          await showSuccess('儲存成功', '視覺配置已更新');
          // 只有在需要時才重新載入（避免上傳後立即重新載入導致 URL 處理問題）
          if (reload) {
            await loadVisualConfig();
          }
        } else {
          await showError('儲存失敗', response?.message || '更新視覺配置失敗');
        }
      } catch (err) {
        console.error('儲存視覺配置失敗:', err);
        await showError('儲存失敗', err.message || '更新視覺配置失敗');
      } finally {
        savingVisualConfig.value = false;
      }
    };

    // 處理圖片選擇
    const handleImageSelect = (fieldName, event) => {
      const file = event.target.files?.[0];
      if (file) {
        uploadImage(fieldName, file);
      }
      event.target.value = '';
    };

    // 上傳圖片
    const uploadImage = async (fieldName, file) => {
      if (!file || !isEdit.value) return;

      try {
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
          await showError('檔案類型錯誤', '僅支援 JPG、PNG、GIF、WebP 格式');
          return;
        }

        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
          await showError('檔案過大', '圖片大小不能超過 5MB');
          return;
        }

        // 使用簽到模組的上傳方法（已改為使用 S3）
        const uploadResponse = await checkinService.uploadImage(configId.value, file);

        if (uploadResponse && uploadResponse.success && uploadResponse.data) {
          // 後端返回的 fileUrl 已經是完整的 S3 URL
          const fileUrl = uploadResponse.data.fileUrl;
          
          if (!fileUrl) {
            await showError('上傳失敗', '未取得圖片 URL');
            return;
          }
          
          if (!visualConfig.value) {
            visualConfig.value = {};
          }
          
          // 直接使用 S3 URL，更新本地狀態
          visualConfig.value[fieldName] = fileUrl;
          
          // 自動保存（不重新載入，避免 URL 處理問題）
          await saveVisualConfig(false);
        } else {
          await showError('上傳失敗', uploadResponse?.message || '圖片上傳失敗');
        }
      } catch (err) {
        console.error('上傳圖片失敗:', err);
        await showError('上傳失敗', err.message || '圖片上傳失敗');
      }
    };

    // 刪除圖片
    const removeImage = async (fieldName) => {
      if (!isEdit.value) return;
      
      const currentImageUrl = visualConfig.value?.[fieldName];
      if (!currentImageUrl) {
        if (visualConfig.value && visualConfig.value[fieldName] !== undefined) {
          delete visualConfig.value[fieldName];
        }
        return;
      }

      try {
        let normalizedUrl = currentImageUrl;
        if (normalizedUrl && (normalizedUrl.startsWith('http://') || normalizedUrl.startsWith('https://'))) {
          try {
            const urlObj = new URL(normalizedUrl);
            normalizedUrl = urlObj.pathname;
          } catch (err) {
            const match = normalizedUrl.match(/\/uploads\/.*/);
            if (match) {
              normalizedUrl = match[0];
            }
          }
        }
        
        // 調用後端 API 刪除圖片文件
        try {
          await checkinService.deleteImage(configId.value, normalizedUrl);
        } catch (deleteErr) {
          // 即使刪除文件失敗，也繼續從配置中移除（用戶體驗）
          console.warn('刪除圖片文件失敗，繼續從配置中移除:', deleteErr);
        }
        
        // 從配置中移除圖片 URL
        if (visualConfig.value) {
          delete visualConfig.value[fieldName];
        }
        
        // 保存配置
        await saveVisualConfig();
      } catch (err) {
        console.error('刪除圖片失敗:', err);
        await showError('刪除失敗', err.message || '圖片刪除失敗');
      }
    };

    // 處理圖片錯誤
    const handleImageError = (fieldName, event) => {
      console.warn(`圖片載入失敗: ${fieldName}`, event);
    };

    // 處理圖片載入
    const handleImageLoad = (fieldName) => {
      console.log(`圖片載入成功: ${fieldName}`);
    };

    // 監聽會員來源變化，檢查抽獎配置是否匹配
    watch(() => form.memberSource, async (newMemberSource, oldMemberSource) => {
      // 如果值沒有改變，不檢查
      if (newMemberSource === oldMemberSource) {
        return;
      }

      // 編輯模式下：檢查抽獎配置是否匹配
      if (isEdit.value) {
        // 如果還沒有完成初始化，不檢查（避免載入時觸發）
        if (!isInitialized.value) {
          return;
        }

        console.log('🔍 會員來源變化:', {
          oldMemberSource,
          newMemberSource,
          dailyCheckinLotteryConfigId: form.dailyCheckinLotteryConfigId,
          rewardsCount: rewards.value.length
        });

        await checkMemberSourceMismatch(newMemberSource);
      }
      
    }, { immediate: false });

    const loadCouponTemplates = async () => {
      try {
        const response = await couponService.getTemplates({ pageSize: 200, isActive: true });
        if (response?.success && response.data) {
          couponTemplates.value = response.data.items || [];
        }
      } catch (err) {
        console.error('載入優惠券範本失敗:', err);
      }
    };

    onMounted(async () => {
      // 載入客戶資訊
      await loadClientInfo();
      
      fetchLotteryConfigs();
      loadCouponTemplates();
      loadConfig();
    });

    return {
      isEdit,
      configId,
      form,
      errors,
      submitting,
      loading,
      activeTab,
      rewards,
      sortedRewards,
      groupedRewardList,
      rewardFormPresetDays,
      loadingRewards,
      lotteryConfigs,
      showRewardModal,
      editingReward,
      submittingReward,
      rewardForm,
      couponTemplates,
      lineMsgTemplate,
      records,
      loadingRecords,
      recordsFilter,
      statistics,
      loadingStats,
      statsFilter,
      visualConfig,
      loadingVisualConfig,
      savingVisualConfig,
      loadVisualConfig,
      saveVisualConfig,
      handleImageSelect,
      handleImageError,
      handleImageLoad,
      removeImage,
      handleSubmit,
      handleCancel,
      handleDailyRewardChange,
      handleAddReward,
      handleAddRewardForDays,
      handleEditReward,
      handleDeleteReward,
      closeRewardModal,
      onMessageTemplateChange,
      handleRewardTypeChange,
      handleSubmitReward,
      fetchRecords,
      handleDeleteRecord,
      fetchStatistics,
      getRewardTypeText,
      isLotteryConfigValid,
      getRewardContent,
      formatDate,
      formatDateOnly,
      isConsecutiveCheckinEnabled,
      checkinRouteUrl,
      checkinTestModeUrl,
      ruleRouteUrl,
      ruleTestModeUrl,
      copyToClipboard,
      clientMemberSystemType,
      isWixtarClient,
      wixtarTickets,
      wixtarTicketsLoading
    };
  }
};
</script>

<style scoped>
/* 使用共用的容器、頁面標題和表單容器樣式，已從 _form-common.scss 引入 */
/* 頁籤、表格、Modal 等共用樣式也已從 _form-common.scss 引入 */

.stats-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
}

.stat-card h5 {
  margin: 0 0 12px 0;
}

/* 獎勵管理樣式 */
.reward-block {
  margin-bottom: 40px;
  padding-bottom: 30px;
  border-bottom: 1px solid #e0e0e0;
}

.reward-block:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

/* 狀態標籤 */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
}

.status-badge i {
  font-size: 16px;
}

.status-badge.status-enabled {
  background: #d1e7dd;
  color: #0f5132;
}

.status-badge.status-enabled i {
  color: #198754;
}

.status-badge.status-disabled {
  background: #f8d7da;
  color: #842029;
}

.status-badge.status-disabled i {
  color: #dc3545;
}

.checkin-toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.checkin-toggle-row.is-invalid .checkin-toggle-slider {
  box-shadow: 0 0 0 1px #dc3545;
}

.checkin-toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.checkin-toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.checkin-toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #ccc;
  border-radius: 24px;
  transition: background 0.2s;
}

.checkin-toggle-slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  top: 3px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.checkin-toggle-switch input:checked + .checkin-toggle-slider {
  background: #4caf50;
}

.checkin-toggle-switch input:checked + .checkin-toggle-slider::before {
  transform: translateX(20px);
}

.checkin-toggle-status {
  font-size: 13px;
  font-weight: 500;
}

.checkin-toggle-status.on {
  color: #4caf50;
}

.checkin-toggle-status.off {
  color: #9ca3af;
}

/* 標題操作區 */
.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* 操作按鈕組 */
.action-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.days-cell {
  vertical-align: top;
  min-width: 110px;
}

.days-cell-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.btn-add-same-day {
  white-space: nowrap;
}

.btn-outline-primary {
  background: #fff;
  color: #3b82f6;
  border: 1px solid #3b82f6;
}

.btn-outline-primary:hover:not(:disabled) {
  background: #eff6ff;
}

/* 警告提示 */
.alert {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
}

.alert i {
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 2px;
}

.alert-warning {
  background: #fff3cd;
  border: 1px solid #ffc107;
  color: #856404;
}

.alert-warning i {
  color: #ffc107;
}

.alert-info {
  background: #d1ecf1;
  border: 1px solid #0dcaf0;
  color: #055160;
}

.alert-info i {
  color: #0dcaf0;
}

.alert-link {
  color: #0dcaf0;
  text-decoration: underline;
  font-weight: 500;
}

.alert-link:hover {
  color: #0aa2c0;
}

/* 累積簽到資訊 */
.cumulative-info {
  margin-bottom: 20px;
  padding: 12px 16px;
  background: #e7f3ff;
  border: 1px solid #b3d9ff;
  border-radius: 6px;
}

.info-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #004085;
}

.info-badge i {
  font-size: 18px;
  color: #0d6efd;
}

.info-badge strong {
  color: #0d6efd;
}

/* 簽到配置頁面特定樣式 */

.stat-value {
  font-size: 32px;
  font-weight: 600;
  color: #007bff;
  margin: 0;
}

.stats-details {
  margin-top: 24px;
}

.stats-details h5 {
  margin-bottom: 16px;
  font-size: 16px;
  font-weight: 600;
}

/* 簽到配置頁面特定樣式 */

/* 篩選列日期區塊 */
.date-range {
  display: flex;
  gap: 12px;
  align-items: center;
  flex: 0 0 auto;
}

.date-range .form-control {
  min-width: 130px;
  max-width: 180px;
  width: auto;
}

.filter-member-search {
  min-width: 160px;
  max-width: 260px;
}

@media (max-width: 768px) {
  .filter-group {
    flex-direction: column;
    align-items: flex-start;
  }

  .filter-group .form-control {
    max-width: 100%;
  }

  .filter-member-search {
    max-width: 100%;
    width: 100%;
  }

  .date-range {
    width: 100%;
    flex: 0 0 auto;
  }

  .date-range .form-control {
    flex: 1;
    min-width: 0;
    max-width: none;
  }

  .date-range .btn {
    flex: 0 0 auto;
  }

  .stats-summary {
    grid-template-columns: 1fr;
  }
}

/* 路由連結區塊 */
.route-links-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.route-link-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.route-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #333;
  font-size: 14px;
  margin: 0;
}

.route-label i {
  font-size: 16px;
  color: #666;
}

.route-input-group {
  display: flex;
  gap: 8px;
  align-items: stretch;
}

.route-input {
  flex: 1;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  background: #f8f9fa;
  cursor: text;
}

.route-input:focus {
  background: #fff;
  border-color: #007bff;
}

.route-actions {
  display: flex;
  gap: 8px;
}

.route-actions .btn {
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
}

.route-actions .btn i {
  font-size: 14px;
}

.subsection-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 24px 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.subsection-title:first-child {
  margin-top: 0;
}

/* 視覺配置編輯器樣式 */
.visual-config-section {
  padding: 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.image-upload-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.image-preview {
  position: relative;
  display: inline-block;
  max-width: 300px;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
  background: #f8f9fa;
}

.image-preview img {
  width: 100%;
  height: auto;
  display: block;
  max-height: 200px;
  object-fit: contain;
}

.btn-remove-image {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  z-index: 10;
}

.btn-remove-image:hover {
  background: rgba(0, 0, 0, 0.8);
}

.btn-remove-image i {
  font-size: 16px;
}

.btn-upload {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #f8f9fa;
  border: 1px dashed #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  color: #666;
}

.btn-upload:hover {
  background: #e9ecef;
  border-color: #007bff;
  color: #007bff;
}

.btn-upload input[type="file"] {
  display: none;
}

.color-input-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

.color-picker {
  width: 60px;
  height: 40px;
  padding: 2px;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
}

.color-text {
  flex: 1;
  font-family: monospace;
}

/* Quill 編輯器樣式 */
.quill-editor {
  min-height: 200px;
}

.quill-editor :deep(.ql-container) {
  min-height: 200px;
  font-size: 14px;
}

.quill-editor :deep(.ql-editor) {
  min-height: 200px;
  line-height: 1.6;
}

/* 調整段落間距，使其更緊湊 */
.quill-editor :deep(.ql-editor p) {
  margin: 4px 0; /* 減少段落間距 */
  line-height: 1.6;
}

.quill-editor :deep(.ql-editor p:first-child) {
  margin-top: 0;
}

.quill-editor :deep(.ql-editor p:last-child) {
  margin-bottom: 0;
}

.quill-editor :deep(.ql-toolbar) {
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  border-bottom: 1px solid #ccc;
}

.quill-editor :deep(.ql-container) {
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}

/* 單選框組樣式 */
.radio-group {
  margin-bottom: 16px;
}

.radio-group:last-child {
  margin-bottom: 0;
}

/* 小型按鈕樣式 */
.btn-sm {
  padding: 4px 12px;
  font-size: 13px;
}

/* 危險按鈕樣式 */
.btn-danger {
  background: #dc3545;
  color: white;
  border: 1px solid #dc3545;
}

.btn-danger:hover {
  background: #bb2d3b;
  border-color: #bb2d3b;
}

/* 刪除記錄按鈕樣式 */
.btn-delete-record {
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  min-width: auto;
}

.btn-delete-record .btn-text {
  display: inline-block;
}

/* 手機版：只顯示圖標 */
@media (max-width: 768px) {
  .btn-delete-record .btn-text {
    display: none;
  }
  
  .btn-delete-record {
    padding: 4px 8px;
    min-width: 32px;
    justify-content: center;
  }
}

.picker-goto-link {
  margin-top: 8px;
  font-size: 12px;
}

.picker-goto-link a {
  color: #3b82f6;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.picker-goto-link a:hover {
  text-decoration: underline;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin-bottom: 4px;
}

.radio-group.is-disabled {
  opacity: 0.65;
}

.radio-group.is-disabled .radio-label {
  cursor: not-allowed;
}

.form-hint.disabled-tip {
  color: #856404;
  background-color: #fff3cd;
  padding: 6px 8px;
  border-radius: 4px;
  border-left: 3px solid #ffc107;
  margin-top: 4px;
}
</style>
